import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { useModalDismiss } from '../../../hooks/useModalDismiss';

const BudgetTemplateModal = ({ isOpen, onClose, currentProjectId, onApply }) => {
  const [projectGroups, setProjectGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedBudgetId, setSelectedBudgetId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedProjectId(currentProjectId);
    setSelectedBudgetId(null);
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get('/projects/budget-templates');
        setProjectGroups(res.data.data || []);
      } catch { }
      finally { setLoading(false); }
    };
    fetch();
  }, [isOpen, currentProjectId]);

  const dismiss = useModalDismiss(onClose, { enabled: isOpen });

  if (!isOpen) return null;

  const currentGroup = projectGroups.find(g => g.project.id === selectedProjectId);
  const budgets = currentGroup?.budgets || [];
  const selectedBudget = budgets.find(b => b.id === selectedBudgetId);

  const handleApply = () => {
    if (!selectedBudget) return;
    onApply(selectedBudget.lines);
    onClose();
  };

  return (
    <div style={overlay} {...dismiss}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FontAwesomeIcon icon={faCopy} style={{ color: '#9ca3af' }} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#111827' }}>Appliquer un modèle</span>
          </div>
          <button onClick={onClose} style={closeBtn}><FontAwesomeIcon icon={faTimes} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af' }}>
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
              <div style={{ fontSize: '0.78rem', marginTop: 6 }}>Chargement des modèles...</div>
            </div>
          ) : projectGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#d1d5db', fontSize: '0.82rem' }}>
              Aucun budget existant à copier.
            </div>
          ) : (
            <>
              {/* Project selector */}
              <div style={{ marginBottom: 16 }}>
                <div style={fieldLabel}>Projet</div>
                <select value={selectedProjectId || ''} onChange={e => { setSelectedProjectId(parseInt(e.target.value)); setSelectedBudgetId(null); }}
                  style={selectStyle}>
                  {projectGroups.map(g => (
                    <option key={g.project.id} value={g.project.id}>
                      {g.project.name} {g.project.id === currentProjectId ? '(ce projet)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Budget selector */}
              <div style={{ marginBottom: 16 }}>
                <div style={fieldLabel}>Enquête source</div>
                {budgets.length === 0 ? (
                  <div style={{ fontSize: '0.78rem', color: '#d1d5db', fontStyle: 'italic' }}>Aucun budget dans ce projet.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {budgets.map(b => (
                      <div
                        key={b.id}
                        onClick={() => setSelectedBudgetId(b.id)}
                        style={{
                          padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                          border: selectedBudgetId === b.id ? '2px solid #dc2626' : '1px solid #e5e7eb',
                          background: selectedBudgetId === b.id ? '#fef2f2' : '#fff',
                          transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#111827' }}>{b.slot_name}</span>
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151' }}>
                            {Number(b.total_cost).toLocaleString('fr-FR')} MAD
                          </span>
                        </div>
                        <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: 2 }}>
                          {b.lines.length} ligne(s) · {b.status === 'approved' ? 'Approuvé' : b.status === 'submitted' ? 'Soumis' : 'Brouillon'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Preview */}
              {selectedBudget && (
                <div>
                  <div style={fieldLabel}>Aperçu des lignes</div>
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6b7280' }}>Catégorie</th>
                          <th style={{ padding: '5px 8px', textAlign: 'left', color: '#6b7280' }}>Libellé</th>
                          <th style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280' }}>Qté/Jr</th>
                          <th style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280' }}>P.U.</th>
                          <th style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBudget.lines.map((l, i) => {
                          const isLump = ['deplacement', 'imprevus', 'autre'].includes(l.category);
                          return (
                          <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '5px 8px' }}>{CATS[l.category] || l.category}</td>
                            <td style={{ padding: '5px 8px', color: '#6b7280' }}>{l.label || '—'}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280' }}>{isLump ? '—' : l.qty}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: '#6b7280' }}>{isLump ? '—' : Number(l.unit_price).toLocaleString('fr-FR')}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{Number(l.total).toLocaleString('fr-FR')}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={cancelBtn}>Annuler</button>
          <button onClick={handleApply} disabled={!selectedBudget}
            style={{
              padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: 600,
              background: selectedBudget ? '#dc2626' : '#e5e7eb',
              color: selectedBudget ? '#fff' : '#9ca3af',
              cursor: selectedBudget ? 'pointer' : 'default',
            }}>
            <FontAwesomeIcon icon={faCopy} size="xs" className="me-1" />
            Appliquer
          </button>
        </div>
      </div>
      <style>{`@keyframes templateModalIn { from { opacity:0; transform:translateY(10px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }`}</style>
    </div>
  );
};

const CATS = {
  enqueteurs_cati: 'Enquêteurs CATI', enqueteurs_f2f: 'Prestataires F2F',
  controle: 'Contrôle', deplacement: 'Déplacement', imprevus: 'Imprévus', autre: 'Autre',
};

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)' };
const modal = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden', animation: 'templateModalIn 0.2s ease' };
const closeBtn = { background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4 };
const cancelBtn = { padding: '7px 16px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: '0.78rem', fontWeight: 500, cursor: 'pointer' };
const fieldLabel = { fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 };
const selectStyle = { width: '100%', fontSize: '0.78rem', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 6, outline: 'none', background: '#fff', cursor: 'pointer' };

export default BudgetTemplateModal;
